// components/Drawer.tsx
import React, {
  useRef,
  useState,
  useEffect,
  PropsWithChildren,
  ReactNode,
  ReactElement,
  Children,
  isValidElement,
} from 'react';
import ReactDOM from 'react-dom';
import { cn } from '@/shared/utils/cn';
import { DrawerProps, DrawerFooterProps } from '@/shared/types/drawer';
import { Card } from '../Cards';
import { Button } from '../Button';
import { Title } from '../Title';
import { Switch } from '../Switch';
import { InnerButton } from '../InnerButton';
import { Tooltip } from '../Tooltip';
import {
  usePortal,
  useAnimationState,
  useBodyScrollLock,
  useEscapeKey,
  useFocusTrap,
} from '@/shared/hooks';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

/**
 * DrawerFooter - Subcomponente de footer para Drawer
 *
 * @internal
 */
const DrawerFooter = ({ children, className }: DrawerFooterProps) => {
  return (
    <Card.Footer
      className={cn('ui-flex ui-items-center ui-justify-end ui-gap-x-16 ui-p-16', className)}
    >
      {children}
    </Card.Footer>
  );
};

// Nombre para identificar el componente
DrawerFooter.displayName = 'DrawerFooter';

// Función auxiliar para extraer el footer de los children
const extractFooterFromChildren = (
  children: ReactNode,
): { content: ReactNode[]; footer: ReactElement | null } => {
  const content: ReactNode[] = [];
  let footer: ReactElement | null = null;

  Children.forEach(children, (child) => {
    if (
      isValidElement(child) &&
      (child.type as { displayName?: string })?.displayName === 'DrawerFooter'
    ) {
      footer = child;
    } else {
      content.push(child);
    }
  });

  return { content, footer };
};
const sizeMap = {
  sm: 'ui-w-[280px]',
  md: 'ui-w-[400px]',
  lg: 'ui-w-[560px]',
  xl: 'ui-w-[800px]',
};

const maxWidthMap = {
  sm: '280px',
  md: '400px',
  lg: '560px',
  xl: '800px',
};

// Tipo para el componente Drawer con subcomponentes
interface DrawerComponent extends React.FC<DrawerProps> {
  Footer: React.FC<DrawerFooterProps>;
}

/**
 * Drawer - Componente de drawer/cajón lateral deslizable
 *
 * Por defecto (`surface="default"`), panel y {@link Card} usan **`ui-bg-surface-secondary-default`**
 * (token `--color-bg-surface-secondary-default`). Con **`surface="brand"`** usan
 * **`ui-bg-fill-brand-default`**, sin borde bajo el header ni separador antes del footer; la
 * {@link Card} interna usa **`bordered={false}`** para no mostrar borde de tarjeta.
 *
 * Componente de drawer que se desliza desde los lados de la pantalla.
 * Soporta múltiples posiciones, tamaños, tabs, botones de acción y footer personalizado.
 *
 * @example
 * ```tsx
 * // Drawer simple
 * <Drawer
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Configuración"
 * >
 *   <div>Contenido del drawer</div>
 * </Drawer>
 *
 * // Drawer con footer personalizado
 * <Drawer
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Editar"
 * >
 *   <div>Contenido</div>
 *   <Drawer.Footer>
 *     <Button onClick={() => setIsOpen(false)}>Cancelar</Button>
 *     <Button onClick={handleSave}>Guardar</Button>
 *   </Drawer.Footer>
 * </Drawer>
 * ```
 *
 * @see {@link DrawerProps} para la lista completa de props
 */
export const Drawer: DrawerComponent = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  titleIcon,
  titleIconDuotonePrimary,
  titleIconDuotoneSecondary,
  titleIconDuotoneOpacityPrimary,
  titleIconDuotoneOpacitySecondary,
  showCloseButton = true,
  closeButton,
  disableEscapeClose = false,
  disableOutsideTab = false,
  position = 'right',
  surface = 'default',
  className,
  scrollable = true,
  cancelButtonText = 'Cancelar',
  successButtonText = 'Guardar',
  onCancel = () => { },
  onSuccess = () => { },
  showCancelButton = true,
  showSuccessButton = true,
  closeAtSuccess = false,
  closeAtCancel = true,
  disabledSuccessButton = false,
  disabledCancelButton = false,
  maxWidth,
  fullWidth = false,
  fitContent = false,
  tabs,
  defaultValueTab,
  onChangeTab,
  currentTab: controlledCurrentTab,
  tabsOverHeader = false,
}) => {
  // Extraer el footer personalizado de los children
  const { content: childrenContent, footer: customFooter } = extractFooterFromChildren(children);

  // Hooks reutilizables
  const container = usePortal();
  const { isVisible, isAnimating } = useAnimationState(isOpen);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Estado interno para tabs (no controlado)
  const [internalCurrentTab, setInternalCurrentTab] = useState<string | undefined>(defaultValueTab);

  // Actualizar el tab interno cuando cambia defaultValueTab
  useEffect(() => {
    if (defaultValueTab !== undefined) {
      setInternalCurrentTab(defaultValueTab);
    }
  }, [defaultValueTab]);

  // Determinar el tab actual (controlado o no controlado)
  const currentTab = controlledCurrentTab ?? internalCurrentTab;

  // Manejar cambio de tab
  const handleChangeTab = (value: string) => {
    if (controlledCurrentTab === undefined) {
      setInternalCurrentTab(value);
    }
    if (onChangeTab) {
      onChangeTab(value);
    }
  };

  // Verificar si hay tabs
  const hasTabs = tabs && tabs.length > 0;

  // Bloquear scroll del body
  useBodyScrollLock(isOpen);

  // Manejar tecla Escape
  useEscapeKey(isOpen, onClose, disableEscapeClose);

  // Focus trap
  useFocusTrap(isOpen, drawerRef, !disableOutsideTab);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }

    if (closeAtCancel) {
      onClose();
    }
  };

  const handleSuccess = () => {
    if (onSuccess) {
      onSuccess();
    }

    if (closeAtSuccess) {
      onClose();
    }
  };

  if (!container || !isVisible) return null;

  const isBrandSurface = surface === 'brand';
  const surfaceBgClass = isBrandSurface
    ? 'ui-bg-fill-brand-default'
    : 'ui-bg-surface-secondary-default';
  const showHeaderFooterDividers = !isBrandSurface;

  const drawerContent = (
    <div
      aria-modal="true"
      role="dialog"
      className="ui-imj ui-w- ui-fixed ui-inset-0 ui-z-50 ui-flex ui-overflow-hidden"
      onClick={onClose}
    >
      {/* Overlay */}
      <div
        className={cn(
          'ui-absolute ui-inset-0 ui-bg-black ui-opacity-50 ui-backdrop-blur-sm ui-transition-opacity ui-duration-300',
          isOpen ? 'ui-opacity-100' : 'ui-opacity-0',
        )}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'ui-relative ui-z-10 ui-flex ui-flex-col ui-shadow-xl ui-outline-none ui-transition-transform ui-duration-300 ui-ease-in-out',
          surfaceBgClass,
          fitContent ? 'ui-w-fit' : fullWidth ? 'ui-w-full' : sizeMap[size],
          position === 'right' ? 'ui-ml-auto' : 'ui-mr-auto',
        )}
        style={{
          transform:
            position === 'right'
              ? isAnimating
                ? 'translateX(0)'
                : 'translateX(100%)'
              : isAnimating
                ? 'translateX(0)'
                : 'translateX(-100%)',
          maxWidth: fitContent ? 'none' : fullWidth ? 'none' : maxWidth || maxWidthMap[size],
        }}
      >
        <Card
          rounded={false}
          padding={false}
          gap={false}
          bordered={!isBrandSurface}
          className={cn(surfaceBgClass, className)}
        >
          <Card.Header>
            <div
              className={cn(
                'ui-relative ui-flex ui-flex-wrap ui-items-center ui-justify-between ui-gap-x-16 ui-gap-y-8 ui-p-16',
                showHeaderFooterDividers && 'ui-border-b ui-border-default',
              )}
            >
              {/* Título */}
              <div className="ui-flex ui-flex-1 ui-items-center ui-gap-8">
                {title && (
                  <Title
                    title={title}
                    icon={titleIcon}
                    iconDuotonePrimary={titleIconDuotonePrimary}
                    iconDuotoneSecondary={titleIconDuotoneSecondary}
                    iconDuotoneOpacityPrimary={titleIconDuotoneOpacityPrimary}
                    iconDuotoneOpacitySecondary={titleIconDuotoneOpacitySecondary}
                    className={isBrandSurface ? 'ui-text-on-color' : undefined}
                  />
                )}
              </div>

              {/* Botón de cerrar */}
              {showCloseButton && (
                <div className="ui-z-10">
                  {closeButton?.tooltip === true ? (
                    <InnerButton
                      onClick={onClose}
                      aria-label="Cerrar drawer"
                      theme="solid"
                      color="secondary"
                      pill="pill"
                      icon={faXmark}
                      size="xs"
                      tooltipProps={{
                        label: closeButton.tooltipLabel ?? 'Cerrar',
                        position: closeButton.tooltipPosition ?? 'left',
                        alignment: closeButton.tooltipAlignment ?? 'center',
                      }}
                    />

                  ) : (
                    <InnerButton
                      onClick={onClose}
                      aria-label="Cerrar drawer"
                      theme="solid"
                      color="secondary"
                      pill="pill"
                      icon={faXmark}
                      size="xs"
                    />
                  )}
                </div>
              )}

              {/* Tabs centrados sobre el header (posición absoluta como en Modal) */}
              {hasTabs && tabsOverHeader && (
                <div className="ui-z-0 ui-mt-2 ui-flex ui-w-full ui-justify-center ui-overflow-x-auto sm:ui-pointer-events-none sm:ui-absolute sm:ui-inset-x-0 sm:ui-top-1/2 sm:ui-mt-0 sm:ui-w-auto sm:ui--translate-y-1/2">
                  <div className="sm:ui-pointer-events-auto">
                    <Switch
                      options={tabs}
                      defaultValue={currentTab}
                      size="sm"
                      onChange={handleChangeTab}
                    />
                  </div>
                </div>
              )}

              {/* Tabs debajo del título (fila completa) */}
              {hasTabs && !tabsOverHeader && (
                <div className="ui-flex ui-w-full ui-justify-center ui-overflow-x-auto">
                  <Switch
                    options={tabs}
                    defaultValue={currentTab}
                    size="sm"
                    onChange={handleChangeTab}
                  />
                </div>
              )}
            </div>
          </Card.Header>
          <Card.Body
            fullHeight
            className={cn('ui-p-16', scrollable ? 'ui-overflow-y-auto' : '')}
            scrollable={scrollable}
          >
            {childrenContent}
          </Card.Body>
          {/* Footer personalizado (Drawer.Footer) o por defecto */}
          {customFooter ? (
            <>
              {showHeaderFooterDividers && <Card.Separator padding={false} />}
              {customFooter}
            </>
          ) : (
            (showCancelButton || showSuccessButton) && (
              <>
                {showHeaderFooterDividers && <Card.Separator padding={false} />}
                <Card.Footer className="ui-flex ui-items-center ui-justify-end ui-gap-x-16 ui-p-16">
                  {showCancelButton && (
                    <Button
                      size="xs"
                      fullWidth
                      theme="solid"
                      color="secondary"
                      pill="sm"
                      onClick={handleCancel}
                      disabled={disabledCancelButton}
                    >
                      {cancelButtonText}
                    </Button>
                  )}
                  {showSuccessButton && (
                    <Button
                      size="xs"
                      fullWidth
                      theme="solid"
                      color="primary"
                      pill="sm"
                      onClick={handleSuccess}
                      disabled={disabledSuccessButton}
                    >
                      {successButtonText}
                    </Button>
                  )}
                </Card.Footer>
              </>
            )
          )}
        </Card>
      </div>
    </div>
  );

  return ReactDOM.createPortal(drawerContent, container);
};

// Asignar subcomponente
Drawer.Footer = DrawerFooter;
